/**
 * Risk dice probability table + EV helpers. Pure data + lookup
 * functions — no game state, no map, no I/O. Used by the AI's deploy
 * and attack heuristics to rank candidate moves by expected value.
 *
 * Why a hand-coded table: the underlying combinatorics are static and
 * tiny (six possible dice-count pairs). Computing them at runtime via
 * Monte Carlo or full enumeration would add complexity for zero
 * accuracy gain. Exact fractions are kept in comments + numerators so
 * the test suite can verify the table without floating-point drift.
 */

export type AttDice = 1 | 2 | 3;
export type DefDice = 1 | 2;

interface Outcome {
  // Units the attacker/defender lose on this outcome. Per Risk rules a
  // single die comparison sheds 1 unit; double-die rolls (2v2, 3v2)
  // shed 2 units total split between sides.
  readonly attLoss: number;
  readonly defLoss: number;
  readonly p: number;
}

type DicePairKey = `${AttDice}v${DefDice}`;

/**
 * Outcome distribution per (attDice, defDice). Each row's probabilities
 * sum to 1. Defender wins ties, which is why even matchups skew toward
 * the defender (e.g. 1v1 attacker wins 15/36, defender wins 21/36).
 */
const TABLE: Readonly<Record<DicePairKey, readonly Outcome[]>> = {
  // 1v1 — 36 outcomes total. Attacker wins iff A > D → 15/36.
  '1v1': [
    { attLoss: 1, defLoss: 0, p: 21 / 36 },
    { attLoss: 0, defLoss: 1, p: 15 / 36 },
  ],
  // 2v1 — 216 outcomes. Attacker takes max of 2 dice vs defender's 1.
  '2v1': [
    { attLoss: 1, defLoss: 0, p: 91 / 216 },
    { attLoss: 0, defLoss: 1, p: 125 / 216 },
  ],
  // 3v1 — 1296 outcomes. Attacker takes max of 3 dice vs defender's 1.
  '3v1': [
    { attLoss: 1, defLoss: 0, p: 441 / 1296 },
    { attLoss: 0, defLoss: 1, p: 855 / 1296 },
  ],
  // 1v2 — 216 outcomes. Defender takes max of 2 dice vs attacker's 1.
  // Symmetric flip of 2v1: defender now has the 2-dice advantage.
  '1v2': [
    { attLoss: 1, defLoss: 0, p: 161 / 216 },
    { attLoss: 0, defLoss: 1, p: 55 / 216 },
  ],
  // 2v2 — 1296 outcomes. Both sort 2 dice desc and pair top/bottom.
  // Ties go to defender on both pairs, which is why "att loses 2" is
  // the most likely single outcome (0.448).
  '2v2': [
    { attLoss: 2, defLoss: 0, p: 581 / 1296 },
    { attLoss: 1, defLoss: 1, p: 420 / 1296 },
    { attLoss: 0, defLoss: 2, p: 295 / 1296 },
  ],
  // 3v2 — 7776 outcomes. Attacker takes top 2 of 3 dice vs defender's 2.
  // The extra die tips the most-likely outcome from "att loses 2" to
  // "def loses 2" (0.372 vs 0.293) — small but enough to make 3v2
  // slightly favorable for the attacker.
  '3v2': [
    { attLoss: 2, defLoss: 0, p: 2275 / 7776 },
    { attLoss: 1, defLoss: 1, p: 2611 / 7776 },
    { attLoss: 0, defLoss: 2, p: 2890 / 7776 },
  ],
};

export interface CombatOdds {
  readonly attLossExpected: number;
  readonly defLossExpected: number;
}

/**
 * Expected unit losses per side for one roll with the given dice
 * counts. Lookup against the static probability table — no math at
 * runtime beyond summing the outcome probabilities.
 */
export function combatOdds(att: AttDice, def: DefDice): CombatOdds {
  // The function signature constrains att/def to the 6 valid pairs, so
  // the table lookup is total — assert non-null to keep TS happy with
  // noUncheckedIndexedAccess.
  const outcomes = TABLE[`${att}v${def}`]!;
  let attLossExpected = 0;
  let defLossExpected = 0;
  for (const o of outcomes) {
    attLossExpected += o.attLoss * o.p;
    defLossExpected += o.defLoss * o.p;
  }
  return { attLossExpected, defLossExpected };
}

/**
 * Return all outcomes for a given (attDice, defDice) pair. Exposed for
 * tests that need to verify the distribution, not currently used by
 * heuristics — those only need expected values.
 */
export function outcomesFor(att: AttDice, def: DefDice): readonly Outcome[] {
  return TABLE[`${att}v${def}`]!;
}

/**
 * Single-roll attack EV from the attacker's perspective: defender's
 * expected loss minus attacker's expected loss. Positive = favorable.
 *
 * Inputs are unit counts on each tile. Picks the maximum dice each
 * side can roll (`min(3, attUnits-1)` for attacker, `min(2, defUnits)`
 * for defender). Returns -Infinity for invalid inputs (attacker must
 * keep 1 unit behind, so `attUnits >= 2`; defender needs at least 1).
 *
 * Reference values:
 *   3v1: +0.319    3v2: +0.158
 *   2v1: +0.157    2v2: -0.441
 *   1v1: -0.167    1v2: -0.491
 */
export function attackEv(attUnits: number, defUnits: number): number {
  if (attUnits < 2 || defUnits < 1) return Number.NEGATIVE_INFINITY;
  const att = Math.min(3, attUnits - 1) as AttDice;
  const def = Math.min(2, defUnits) as DefDice;
  const odds = combatOdds(att, def);
  return odds.defLossExpected - odds.attLossExpected;
}

/** Maximum dice the attacker can roll given the unit count on `from`. */
export function maxAttackDice(attUnits: number): AttDice {
  if (attUnits < 2) return 1; // Caller should gate; safe fallback.
  return Math.min(3, attUnits - 1) as AttDice;
}

/** Maximum dice the defender rolls given the unit count on `to`. */
export function maxDefenseDice(defUnits: number): DefDice {
  if (defUnits < 1) return 1; // Same defensive fallback.
  return Math.min(2, defUnits) as DefDice;
}
